import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Eye, Pencil, Trash2, Phone, MapPin, AlertTriangle, FileText, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PatientForm } from "@/pages/Patients";
import IpdSection from "@/components/patient/IpdSection";
import LabReports from "@/components/patient/LabReports";
import Radiology from "@/components/patient/Radiology";
import PregnancyTab from "@/components/patient/PregnancyTab";
import MedHistory from "@/components/patient/MedHistory";
import GynaeTools from "@/components/patient/GynaeTools";
import ScreeningsTab from "@/components/patient/ScreeningsTab";

export default function PatientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);
    const [visits, setVisits] = useState(null);
    const [showEdit, setShowEdit] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "visits");

    const load = async () => {
        try {
            const [{ data: p }, { data: v }] = await Promise.all([
                api.get(`/patients/${id}`),
                api.get(`/patients/${id}/visits`),
            ]);
            setPatient(p);
            setVisits(v);
        } catch (e) {
            toast.error(fmtErr(e));
            navigate("/patients");
        }
    };

    useEffect(() => { load(); }, [id]);

    const saveEdit = async (payload) => {
        setSaving(true);
        try {
            const { data } = await api.put(`/patients/${id}`, payload);
            setPatient(data);
            setShowEdit(false);
            toast.success("Patient updated");
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete ${patient.name} and all their visits? This cannot be undone.`)) return;
        try {
            await api.delete(`/patients/${id}`);
            toast.success("Patient deleted");
            navigate("/patients");
        } catch (e) {
            toast.error(fmtErr(e));
        }
    };

    if (!patient) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E06D1F]" data-testid="patient-loading" /></div>;

    return (
        <div className="space-y-5 animate-fade-up" data-testid="patient-detail-page">
            <button onClick={() => navigate("/patients")} data-testid="back-to-patients-btn" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#E06D1F]">
                <ArrowLeft className="h-4 w-4" /> All Patients
            </button>

            <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="patient-profile-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{patient.name}</h1>
                            <span className="rounded-full bg-[#FFF4EC] border border-[#F6C9A4] px-2.5 py-0.5 text-xs font-mono2 font-medium text-[#C2410C]" data-testid="patient-uid-badge">{patient.patient_uid}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
                            <span>{patient.age ? `${patient.age} yrs` : "Age —"} · {patient.gender || "—"}{patient.blood_group ? ` · ${patient.blood_group}` : ""}</span>
                            {patient.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{patient.phone}</span>}
                            {patient.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{patient.address}</span>}
                            {patient.referred_by && <span data-testid="patient-referred-by">Ref: {patient.referred_by}{patient.referred_date ? ` (${patient.referred_date})` : ""}</span>}
                            {patient.preferred_language && <span data-testid="patient-language">Lang: {patient.preferred_language}</span>}
                            {patient.assigned_doctor_name && <span data-testid="patient-assigned-doctor" className="font-medium text-[#C2410C]">Assigned: {patient.assigned_doctor_name}</span>}
                            {patient.abha_id && <span data-testid="patient-abha-badge" className="inline-flex items-center gap-1 rounded-md bg-teal-50 border border-teal-200 px-2 py-0.5 text-xs font-medium text-teal-700">ABHA: {patient.abha_id}</span>}
                        </div>
                        {patient.allergies && (
                            <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-1 text-xs font-medium text-red-700" data-testid="patient-allergies-alert">
                                <AlertTriangle className="h-3.5 w-3.5" /> Allergies: {patient.allergies}
                            </p>
                        )}
                        {patient.has_insurance === "yes" && (
                            <p className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-lg bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-700" data-testid="patient-insurance-badge">
                                Insured: {patient.insurance_company || "Yes"}{patient.payment_mode ? ` · ${patient.payment_mode}` : ""}
                            </p>
                        )}
                        {patient.notes && <p className="mt-2 text-sm text-slate-500" data-testid="patient-notes">History: {patient.notes}</p>}
                    </div>
                    <div className="flex gap-2">
                        <Button data-testid="edit-patient-btn" variant="outline" size="sm" onClick={() => setShowEdit(true)} className="gap-1.5"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                        <Button data-testid="delete-patient-btn" variant="outline" size="sm" onClick={remove} className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
                        <Link to={`/appointments?new=1&pid=${id}&pname=${encodeURIComponent(patient.name)}`} data-testid="book-appointment-btn">
                            <Button size="sm" variant="outline" className="gap-1.5"><CalendarPlus className="h-4 w-4" /> Book Appointment</Button>
                        </Link>
                        <Link to={`/patients/${id}/visits/new`} data-testid="new-visit-btn">
                            <Button size="sm" className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Plus className="h-4 w-4" /> New Visit</Button>
                        </Link>
                    </div>
                </div>
            </div>

            <div className="flex gap-1 border-b border-slate-200" data-testid="patient-tabs">
                {[["visits", "Visits"], ["meds", "Medications"], ["pregnancy", "Pregnancy (ANC)"], ["gynae", "Gynae Tools"], ["screenings", "Screenings"], ["ipd", "IPD"], ["radiology", "Radiology"], ["reports", "Lab Reports"]].map(([k, l]) => (
                    <button key={k} data-testid={`patient-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 ${tab === k ? "border-[#E06D1F] text-[#C2410C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
                ))}
            </div>

            {tab === "visits" && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="visit-timeline-card">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-heading font-semibold text-slate-900">Visit History</h3>
                    <span className="text-xs text-slate-500">{visits ? `${visits.length} visit${visits.length === 1 ? "" : "s"}` : ""}</span>
                </div>
                {!visits ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                ) : visits.length === 0 ? (
                    <div className="px-5 py-12 text-center">
                        <FileText className="h-8 w-8 text-slate-300 mx-auto" />
                        <p className="mt-3 text-sm text-slate-500" data-testid="no-visits-text">No consultations yet.</p>
                        <Link to={`/patients/${id}/visits/new`} data-testid="first-visit-btn" className="mt-2 inline-block text-sm font-medium text-[#C2410C] hover:underline">Start first consultation</Link>
                    </div>
                ) : (
                    <div className="relative px-5 py-4">
                        <div className="absolute left-[29px] top-6 bottom-6 w-px bg-slate-200" />
                        <div className="space-y-4">
                            {visits.map((v) => (
                                <div key={v.id} data-testid={`visit-card-${v.visit_no}`} className="relative pl-12">
                                    <span className="absolute left-2.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#E06D1F] ring-4 ring-white" />
                                    <div className="rounded-lg border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all duration-150 p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">Visit #{v.visit_no} <span className="font-normal text-slate-400">· {v.date?.slice(0, 10)}</span></p>
                                                <p className="text-xs text-slate-500 mt-0.5">{v.diagnosis || v.symptoms || "General consultation"}</p>
                                                {v.medicines?.length > 0 && <p className="text-xs text-[#C2410C] mt-1">{v.medicines.length} medicine{v.medicines.length === 1 ? "" : "s"} prescribed</p>}
                                            </div>
                                            <Link to={`/visits/${v.id}`} data-testid={`view-rx-btn-${v.visit_no}`}>
                                                <Button variant="outline" size="sm" className="gap-1.5"><Eye className="h-3.5 w-3.5" /> View Rx</Button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            )}

            {tab === "pregnancy" && <PregnancyTab patientId={id} patient={patient} onUpdate={load} />}
            {tab === "meds" && <MedHistory patientId={id} />}
            {tab === "gynae" && <GynaeTools patientId={id} patient={patient} />}
            {tab === "screenings" && <ScreeningsTab patientId={id} />}
            {tab === "ipd" && <IpdSection patientId={id} patient={patient} />}
            {tab === "radiology" && <Radiology patientId={id} />}
            {tab === "reports" && <LabReports patientId={id} />}

            <Dialog open={showEdit} onOpenChange={setShowEdit}>
                <DialogContent className="max-w-2xl" data-testid="edit-patient-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Edit Patient — {patient.patient_uid}</DialogTitle>
                        <DialogDescription>Update the patient's details below.</DialogDescription>
                    </DialogHeader>
                    <PatientForm testPrefix="edit-patient" submitLabel="Save Changes" loading={saving} initial={{ ...patient, age: patient.age ?? "" }} onSubmit={saveEdit} />
                </DialogContent>
            </Dialog>
        </div>
    );
}
